import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Package, CheckCircle, Clock, Plus, Mail, Send, Bell, Calendar, UserPlus, Wrench, Trash2, MapPin, Wifi, Archive, Upload, FileText } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState(null);
  const [fixingFamilies, setFixingFamilies] = useState(false);
  const [showPurgeDialog, setShowPurgeDialog] = useState(false);
  const [purgeEmails, setPurgeEmails] = useState('');
  const [purging, setPurging] = useState(false);
  const [purgeResults, setPurgeResults] = useState(null);
  const [testingImpactMap, setTestingImpactMap] = useState(false);
  const [resyncingImpactMap, setResyncingImpactMap] = useState(false);
  const [showHistoricalDialog, setShowHistoricalDialog] = useState(false);
  const [historicalFile, setHistoricalFile] = useState(null);
  const [uploadingHistorical, setUploadingHistorical] = useState(false);
  const [historicalResults, setHistoricalResults] = useState(null);
  const [downloadingGuide, setDownloadingGuide] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchPermissions();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/reports/statistics');
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await api.get('/auth/permissions');
      setPermissions(response.data.permissions);
    } catch (error) {
      console.error('Failed to fetch permissions');
    }
  };

  const handleFixStuckFamilies = async () => {
    setFixingFamilies(true);
    try {
      const response = await api.post('/admin/fix-stuck-families');
      const { fixed_count, families, message } = response.data;
      if (fixed_count > 0) {
        toast.success(`Fixed ${fixed_count} families: ${families.join(', ')}`);
        fetchStats();
      } else {
        toast.info(message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fix stuck families');
    } finally {
      setFixingFamilies(false);
    }
  };

  const handlePurgeAccounts = async () => {
    const emails = purgeEmails
      .split(/[\n,]+/)
      .map(e => e.trim())
      .filter(e => e.length > 0 && e.includes('@'));
    
    if (emails.length === 0) {
      toast.error('Please enter at least one valid email address');
      return;
    }
    
    setPurging(true);
    setPurgeResults(null);
    try {
      const response = await api.post('/admin/purge-test-accounts', { emails });
      setPurgeResults(response.data.results);
      toast.success(response.data.message);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to purge accounts');
    } finally {
      setPurging(false);
    }
  };

  const handleTestImpactMap = async () => {
    setTestingImpactMap(true);
    try {
      const response = await api.post('/admin/impact-map/test-ping');
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impact Map test ping failed');
    } finally {
      setTestingImpactMap(false);
    }
  };

  const handleResyncImpactMap = async () => {
    setResyncingImpactMap(true);
    try {
      const response = await api.post('/admin/impact-map/resync-all');
      const { sent, failed, total } = response.data;
      if (failed > 0) {
        toast.warning(`Resynced ${sent}/${total} families. ${failed} failed (likely geocoding misses) — see backend logs.`);
      } else {
        toast.success(`Resynced ${sent} families to Impact Map.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impact Map resync failed');
    } finally {
      setResyncingImpactMap(false);
    }
  };

  const handleUploadHistorical = async () => {
    if (!historicalFile) {
      toast.error('Please choose an Excel file first');
      return;
    }
    setUploadingHistorical(true);
    setHistoricalResults(null);
    try {
      const formData = new FormData();
      formData.append('file', historicalFile);
      const response = await api.post('/families/import-historical', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setHistoricalResults(response.data);
      toast.success(response.data.message);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Historical import failed');
    } finally {
      setUploadingHistorical(false);
    }
  };

  const handleDownloadRegistrationGuide = async () => {
    setDownloadingGuide(true);
    try {
      const response = await api.get('/admin/registration-guide-pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `family_registration_guide_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Registration guide downloaded');
    } catch (error) {
      toast.error('Failed to download registration guide');
    } finally {
      setDownloadingGuide(false);
    }
  };

  return (
    <div className="flex" data-testid="admin-dashboard">
      <Sidebar />
      <div className="ml-64 flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-foreground mb-2">Dashboard</h1>
              <p className="text-stone-600">Welcome to The Bookshelf Project</p>
            </div>
            <Link to="/families/add">
              <Button className="rounded-full" data-testid="add-family-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Family
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : (
            <>
              {/* Main Stats Row */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-6">
                <Card className="border-green-200 bg-green-50 shadow-card" data-testid="pending-registrations-card">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-green-800">New Registrations</CardTitle>
                    <UserPlus className="w-5 h-5 text-green-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-green-900">{stats?.pending_registrations || 0}</div>
                    {stats?.pending_registrations > 0 && (
                      <Link to="/new-families" className="text-xs text-green-700 hover:underline">Review now →</Link>
                    )}
                  </CardContent>
                </Card>

                <Card className="border-stone-200 shadow-card" data-testid="total-families-card">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-stone-600">Total Families</CardTitle>
                    <Users className="w-5 h-5 text-primary" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-foreground">{stats?.total_families || 0}</div>
                  </CardContent>
                </Card>

                <Card className="border-stone-200 shadow-card" data-testid="delivered-card">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-stone-600">Delivered</CardTitle>
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-foreground">{stats?.delivered || 0}</div>
                  </CardContent>
                </Card>

                <Card className="border-stone-200 shadow-card" data-testid="pending-card">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-stone-600">Awaiting Delivery</CardTitle>
                    <Clock className="w-5 h-5 text-yellow-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-foreground">{stats?.pending || 0}</div>
                  </CardContent>
                </Card>

                <Card className="border-stone-200 shadow-card" data-testid="total-children-card">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-stone-600">Total Children</CardTitle>
                    <Package className="w-5 h-5 text-secondary" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-foreground">{stats?.total_children || 0}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Email Workflow Stats Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <Card className="border-amber-200 bg-amber-50 shadow-card" data-testid="awaiting-welcome-card">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-amber-800">Awaiting Welcome Letter</CardTitle>
                    <Mail className="w-5 h-5 text-amber-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-amber-900">{stats?.awaiting_welcome || 0}</div>
                    <p className="text-xs text-amber-700 mt-1">Families who haven't received their welcome email yet</p>
                    {(stats?.awaiting_welcome || 0) > 0 && (
                      <Link to="/scheduling-invitations">
                        <Button size="sm" variant="outline" className="mt-3 border-amber-300 text-amber-800 hover:bg-amber-100">
                          <Mail className="w-3 h-3 mr-1" />
                          Send Welcome Emails
                        </Button>
                      </Link>
                    )}
                  </CardContent>
                </Card>

                <Card className="border-purple-200 bg-purple-50 shadow-card" data-testid="ready-to-schedule-card">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-purple-800">Ready to Schedule</CardTitle>
                    <Bell className="w-5 h-5 text-purple-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-purple-900">{stats?.ready_to_schedule || 0}</div>
                    <p className="text-xs text-purple-700 mt-1">Families who clicked "Ready to Schedule" in their portal</p>
                    {(stats?.ready_to_schedule || 0) > 0 && (
                      <Link to="/scheduling-invitations">
                        <Button size="sm" variant="outline" className="mt-3 border-purple-300 text-purple-800 hover:bg-purple-100">
                          <Send className="w-3 h-3 mr-1" />
                          Send Invitations
                        </Button>
                      </Link>
                    )}
                  </CardContent>
                </Card>

                <Card className="border-blue-200 bg-blue-50 shadow-card" data-testid="scheduled-delivery-card">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-blue-800">Scheduled Delivery</CardTitle>
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-blue-900">{stats?.scheduled_delivery || 0}</div>
                    <p className="text-xs text-blue-700 mt-1">Families who have scheduled their delivery</p>
                    <Link to="/scheduled-deliveries">
                      <Button size="sm" variant="outline" className="mt-3 border-blue-300 text-blue-800 hover:bg-blue-100">
                        <Calendar className="w-3 h-3 mr-1" />
                        View Deliveries
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* New Self-Registrations Card */}
                <Card className="border-green-200 bg-green-50 shadow-card" data-testid="self-registrations-card">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-green-800">New Self-Registrations</CardTitle>
                    <UserPlus className="w-5 h-5 text-green-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-green-900 mb-2">{stats?.total_self_registered || 0}</div>
                    <p className="text-xs text-green-700 mb-3">Families who registered themselves</p>
                    
                    {stats?.recent_registrations && stats.recent_registrations.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        <p className="text-xs font-medium text-green-800 border-b border-green-200 pb-1">Recent Registrations:</p>
                        {stats.recent_registrations.map((day) => (
                          <div key={day.date} className="bg-white rounded-lg p-2 border border-green-100">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-green-900">{day.date}</span>
                              <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full">{day.count} new</span>
                            </div>
                            <div className="text-xs text-green-700 mt-1">
                              {day.families.map((f, i) => f.name).join(', ')}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-green-600 italic">No recent self-registrations</p>
                    )}
                  </CardContent>
                </Card>

                <Card className="border-stone-200 shadow-card">
                  <CardHeader>
                    <CardTitle>Location Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3" data-testid="location-distribution">
                      {stats?.location_distribution && Object.entries(stats.location_distribution).map(([location, count]) => (
                        <div key={location} className="flex justify-between items-center">
                          <span className="text-stone-600">{location}</span>
                          <span className="font-bold text-foreground">{count}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <Card className="border-stone-200 shadow-card">
                  <CardHeader>
                    <CardTitle>Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Link to="/families" className="block">
                      <Button variant="outline" className="w-full justify-start" data-testid="view-families-button">
                        <Users className="w-4 h-4 mr-2" />
                        View All Families
                      </Button>
                    </Link>
                    <Link to="/inventory" className="block">
                      <Button variant="outline" className="w-full justify-start" data-testid="manage-inventory-button">
                        <Package className="w-4 h-4 mr-2" />
                        Manage Inventory
                      </Button>
                    </Link>
                    {permissions?.can_view_reports && (
                      <Link to="/reports" className="block">
                        <Button variant="outline" className="w-full justify-start" data-testid="view-reports-button">
                          <CheckCircle className="w-4 h-4 mr-2" />
                          View Reports
                        </Button>
                      </Link>
                    )}
                    <Button
                      variant="outline"
                      className="w-full justify-start border-amber-200 text-amber-800 hover:bg-amber-50"
                      onClick={handleFixStuckFamilies}
                      disabled={fixingFamilies}
                      data-testid="fix-stuck-families-button"
                    >
                      <Wrench className="w-4 h-4 mr-2" />
                      {fixingFamilies ? 'Fixing...' : 'Fix Stuck Families'}
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start border-red-200 text-red-700 hover:bg-red-50"
                      onClick={() => { setShowPurgeDialog(true); setPurgeResults(null); }}
                      data-testid="purge-accounts-button"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Purge Test Accounts
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start border-amber-200 text-amber-800 hover:bg-amber-50"
                      onClick={() => { setShowHistoricalDialog(true); setHistoricalFile(null); setHistoricalResults(null); }}
                      data-testid="import-historical-button"
                    >
                      <Archive className="w-4 h-4 mr-2" />
                      Import Historical Families
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start border-stone-200 text-stone-700 hover:bg-stone-50"
                      onClick={handleDownloadRegistrationGuide}
                      disabled={downloadingGuide}
                      data-testid="download-registration-guide-button"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      {downloadingGuide ? 'Generating PDF...' : 'Download Family Registration Guide (PDF)'}
                    </Button>
                    <div className="pt-2 mt-2 border-t border-stone-200">
                      <p className="text-xs text-stone-500 mb-2 px-1">Impact Map (public map at thebookshelfproject.org)</p>
                      <Button
                        variant="outline"
                        className="w-full justify-start border-blue-200 text-blue-700 hover:bg-blue-50 mb-2"
                        onClick={handleTestImpactMap}
                        disabled={testingImpactMap}
                        data-testid="test-impact-map-button"
                      >
                        <Wifi className="w-4 h-4 mr-2" />
                        {testingImpactMap ? 'Pinging...' : 'Test Impact Map Webhook'}
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full justify-start border-emerald-200 text-emerald-800 hover:bg-emerald-50"
                        onClick={handleResyncImpactMap}
                        disabled={resyncingImpactMap}
                        data-testid="resync-impact-map-button"
                      >
                        <MapPin className="w-4 h-4 mr-2" />
                        {resyncingImpactMap ? 'Resyncing all families...' : 'Resync All Families to Impact Map'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>

        {/* Purge Test Accounts Dialog */}
        <Dialog open={showPurgeDialog} onOpenChange={setShowPurgeDialog}>
          <DialogContent className="sm:max-w-lg" data-testid="purge-dialog">
            <DialogHeader>
              <DialogTitle className="text-red-700">Purge Test Accounts</DialogTitle>
              <DialogDescription>
                Permanently remove test accounts from the system. This deletes the user account, family profile, delivery schedules, and email logs for each email.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                <strong>Warning:</strong> This action cannot be undone. Your admin account will be protected and cannot be deleted.
              </div>
              <div className="space-y-2">
                <Label htmlFor="purge-emails">Email addresses (one per line or comma-separated)</Label>
                <textarea
                  id="purge-emails"
                  value={purgeEmails}
                  onChange={(e) => setPurgeEmails(e.target.value)}
                  placeholder={"test1@example.com\ntest2@example.com\ntest3@example.com"}
                  className="w-full h-40 px-3 py-2 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 text-sm font-mono resize-none"
                  data-testid="purge-emails-input"
                />
              </div>
              {purgeResults && (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  <Label>Results:</Label>
                  {purgeResults.map((r, i) => (
                    <div key={i} className={`text-sm p-2 rounded ${
                      r.status.startsWith('DELETED') ? 'bg-green-50 text-green-800' :
                      r.status.startsWith('SKIPPED') ? 'bg-amber-50 text-amber-800' :
                      'bg-stone-50 text-stone-600'
                    }`}>
                      <span className="font-medium">{r.email}:</span> {r.status}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setShowPurgeDialog(false)}
                className="rounded-full"
              >
                {purgeResults ? 'Done' : 'Cancel'}
              </Button>
              {!purgeResults && (
                <Button
                  variant="destructive"
                  onClick={handlePurgeAccounts}
                  disabled={purging || !purgeEmails.trim()}
                  className="rounded-full"
                  data-testid="confirm-purge-button"
                >
                  {purging ? 'Purging...' : 'Purge Accounts'}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Import Historical Families Dialog */}
        <Dialog open={showHistoricalDialog} onOpenChange={setShowHistoricalDialog}>
          <DialogContent className="max-w-2xl" data-testid="historical-dialog">
            <DialogHeader>
              <DialogTitle className="text-amber-800">Import Historical Families</DialogTitle>
              <DialogDescription>
                Upload an Excel spreadsheet of families who received their bookshelves
                <strong> before the portal existed</strong>. Each row will be added as a
                delivered family with a <em>Historical</em> badge. No emails are sent.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                <p className="font-medium mb-1">Expected columns (in any order):</p>
                <p className="text-xs leading-relaxed">
                  First name · Last name · Email · Phone Number · DC Ward · Maryland city/county ·
                  Virginia city/county · Address · Child's First Name · Age · Learning Stage ·
                  Girl or Boy · Reading level · Favorite interests · Library card · Already has
                  bookshelf · Books From Birth signup · Books count · Referral source ·
                  Annual income · Photo/video consent
                </p>
              </div>
              <div>
                <Label htmlFor="historical-file">Excel file (.xlsx)</Label>
                <Input
                  id="historical-file"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setHistoricalFile(e.target.files?.[0] || null)}
                  className="mt-1"
                  data-testid="historical-file-input"
                />
                {historicalFile && (
                  <p className="text-xs text-stone-600 mt-1">Selected: {historicalFile.name}</p>
                )}
              </div>
              {historicalResults && (
                <div className="bg-stone-50 rounded-lg p-3 text-sm space-y-1">
                  <p><strong>Imported:</strong> {historicalResults.imported}</p>
                  <p><strong>Skipped:</strong> {historicalResults.skipped}</p>
                  {historicalResults.errors?.length > 0 && (
                    <details className="text-xs text-stone-600">
                      <summary className="cursor-pointer">View {historicalResults.errors.length} error{historicalResults.errors.length === 1 ? '' : 's'}</summary>
                      <ul className="mt-1 list-disc pl-4">
                        {historicalResults.errors.map((err, i) => (<li key={i}>{err}</li>))}
                      </ul>
                    </details>
                  )}
                  {historicalResults.next_step && (
                    <p className="text-xs text-emerald-700 mt-2">→ {historicalResults.next_step}</p>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowHistoricalDialog(false)}>
                Close
              </Button>
              <Button
                onClick={handleUploadHistorical}
                disabled={!historicalFile || uploadingHistorical}
                data-testid="upload-historical-button"
                className="bg-amber-700 hover:bg-amber-800"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploadingHistorical ? 'Uploading...' : 'Upload & Import'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
